import { LeftArrow } from "neetoicons";
import { Button, Typography } from "neetoui";
import { Form } from "neetoui/formik";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import EmailPreview from "components/EmailPreview";
import MobilePreviewHeader from "components/MobilePreviewHeader";

import EmailForm from "./Form";
import "./share-via-email.scss";
import { emailFormValidationSchema, generateInitialValues } from "./utils";

const ShareViaEmail = ({
  showSendFromField = false,
  backToUrl,
  handleSubmit,
  children,
  description,
  defaultValues,
  disableReplyTo,
  showVideoEmbedInEditor,
  title = "",
}) => {
  const { t } = useTranslation();

  const shareViaEmailTitle = title || t("neetoMolecules.shareViaEmail.title");
  const sendToEmails = notifyEmails => notifyEmails.map(email => email?.value);

  return (
    <div className="w-full">
      <Form
        scrollToErrorField
        formProps={{ noValidate: true }}
        formikProps={{
          enableReinitialize: true,
          validateOnMount: true,
          initialValues: generateInitialValues(defaultValues),
          validationSchema: emailFormValidationSchema(disableReplyTo),
          onSubmit: handleSubmit,
        }}
      >
        {({ values }) => (
          <div className="neeto-molecules-share-frame grid grid-cols-1 md:grid-cols-12">
            <div
              className="neeto-molecules-share-frame__left-wrap col-span-1 p-6 pb-0 md:col-span-6 lg:col-span-5"
              data-testid="share-via-email-frame-wrapper"
            >
              {backToUrl && (
                <Button
                  className="mb-4"
                  data-testid="back-to-url-button"
                  icon={LeftArrow}
                  iconPosition="left"
                  label={t("neetoMolecules.shareViaEmail.backToShare")}
                  size="small"
                  style="link"
                  to={backToUrl}
                />
              )}
              <div className="mb-6">
                <Typography
                  className="mb-0.5 break-words"
                  data-testid="share-via-email-heading"
                  lineHeight="normal"
                  style="h2"
                  weight="semibold"
                >
                  {shareViaEmailTitle}
                </Typography>
                {description && (
                  <Typography
                    className="neeto-ui-text-gray-700"
                    data-testid="share-via-email-sub-heading"
                    lineHeight="normal"
                    style="body2"
                  >
                    {description}
                  </Typography>
                )}
              </div>
              <EmailForm
                {...{
                  disableReplyTo,
                  showSendFromField,
                  showVideoEmbedInEditor,
                }}
              >
                {children}
              </EmailForm>
            </div>
            <MobilePreviewHeader className="col-span-1 md:hidden" />
            <div className="neeto-molecules-share-frame__right-wrap neeto-ui-bg-gray-100 col-span-1 p-6 py-10 md:col-span-6 lg:col-span-7">
              <EmailPreview
                actionButtonText={values.actionButtonText}
                body={values.emailBody}
                subject={values.emailSubject}
                to={sendToEmails(values.notifyEmails)}
              />
            </div>
          </div>
        )}
      </Form>
    </div>
  );
};

ShareViaEmail.propTypes = {
  /**
   * The path to navigate back to the required page on clicking the 'Back to share' button.
   */
  backToUrl: PropTypes.string,
  /**
   * The function that handles submitting of values to the API.
   */
  handleSubmit: PropTypes.func,
  /**
   * React components that can be passed on as children, to add optional fields.
   */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /**
   * Description text to be displayed under the component title.
   */
  description: PropTypes.string,
  /**
   * An object to assign default values to the email form.
   */
  defaultValues: PropTypes.shape({
    sendFromEmail: PropTypes.string,
    replyToEmail: PropTypes.string,
    actionButtonText: PropTypes.string,
    emailSubject: PropTypes.string,
    emailBody: PropTypes.string,
  }),
  /**
   * Boolean value to show the "Send From" field if needed.
   */
  showSendFromField: PropTypes.bool,
  /**
   * Boolean value to hide the "Reply to" field if not needed.
   */
  disableReplyTo: PropTypes.bool,
  /**
   * Boolean value to show video url as embed video in email body.
   */
  showVideoEmbedInEditor: PropTypes.bool,
  /**
   * To specify the title of the component. Defaults to "Share via email"
   */
  title: PropTypes.string,
};

export default ShareViaEmail;
